// covered-by: frontends/carrie/src/ask-v2/review/__tests__/rememberedSentence.test.ts

/** The tag dimensions a rejection can be scoped by when it names no brand. */
export const SCOPE_DIMENSIONS = [
  "markets",
  "productForms",
  "claimTypes",
  "regulations",
] as const;

export type ScopeDimension = (typeof SCOPE_DIMENSIONS)[number];

export type RejectionScope = {
  brand?: string;
  dimension?: ScopeDimension;
  value?: string;
};

/**
 * Whether a scope is specific enough to learn from.
 *
 * why it can be unanswerable: a rejection with no scope binds the whole
 * enterprise, so one wrong click quietly degrades every other brand's review.
 * The reviewer is asked rather than having the widest answer picked for her.
 */
export function isAnswerableScope(scope: RejectionScope): boolean {
  if (scope.brand?.trim()) return true;
  return Boolean(scope.dimension && scope.value?.trim());
}

/**
 * The sentence shown as "here is what I will remember".
 *
 * MUST stay identical to `remembered_sentence` in
 * `services/warhol/contracts/judgments.py`: the string she approves here is
 * the string stored on the memory, and two implementations that drift make the
 * product promise something it did not save.
 */
/** Everything from the remediation marker on: she rejected the CLAIM, not the fix. */
const FIX_ONWARDS = /\*\*\s*fix\s*:?\s*\*\*:?[\s\S]*/i;
const FINDING_LABELS =
  /\*\*\s*(issue|fix|reason|artwork shows)\s*:?\s*\*\*:?/gi;
const MAX_REMEMBERED = 240;

/**
 * The defect a finding asserts, without markers, remediation or newlines.
 *
 * why: mirrors `claim_of` in services/warhol/contracts/judgments.py. The two
 * must agree, because this renders the sentence she approves and that one
 * stores the sentence Puntt keeps -- the first live rejection on the epic
 * stored the whole markdown body, `**Issue:** ... **Fix:** ...`, which is
 * unreadable in the Knowledge Database and at the moment she is asked to
 * confirm it.
 */
export function claimOf(findingText: string): string {
  const cleaned = findingText
    .replace(FIX_ONWARDS, "")
    .replace(FINDING_LABELS, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\.+$/, "");
  if (cleaned.length <= MAX_REMEMBERED) return cleaned;
  return `${cleaned
    .slice(0, MAX_REMEMBERED)
    .replace(/\s\S*$/, "")
    .replace(/[,;:]$/, "")}...`;
}

export function rememberedSentence(
  findingText: string,
  scope: RejectionScope,
): string {
  let where = scope.brand?.trim() ?? "";
  if (!where && scope.dimension && scope.value?.trim()) {
    where = `${scope.value.trim()} (${scope.dimension})`;
  }
  const trimmed = claimOf(findingText);
  return where
    ? `For ${where}, "${trimmed}" is not a defect.`
    : `"${trimmed}" is not a defect.`;
}

/**
 * The brand a finding's cited rule is scoped to, or "" when it names none.
 *
 * why: without this the reject prompt never receives a brand, every rejection
 * is stored with an empty scope, and warhol's `is_answerable` then refuses to
 * learn from ANY of them -- the feature reads as working, click by click, and
 * suppresses nothing on the next artwork. The rule's own brand is the only
 * scope the comment carries; a finding with no cited rule still rejects, it
 * just does not teach (see `AskV2ReviewRejectScopePrompt`).
 */
export function citedRuleBrand(
  citations: readonly unknown[] | undefined,
): string {
  for (const citation of citations ?? []) {
    const brand = (citation as { brand?: unknown } | null)?.brand;
    if (typeof brand === "string" && brand.trim()) return brand.trim();
  }
  return "";
}

/**
 * The scope choices a finding's cited rules offer, for a rejection that names
 * no brand: every tag under the four scope dimensions, deduped, in citation
 * order.
 *
 * why: without these the reject prompt has nothing to ask, and a rejection of
 * a brand-less finding is stored unscoped -- which warhol refuses to learn from.
 */
export function citedRuleScopeChoices(
  citations: readonly unknown[] | undefined,
): { dimension: ScopeDimension; value: string }[] {
  const out: { dimension: ScopeDimension; value: string }[] = [];
  const seen = new Set<string>();
  for (const citation of citations ?? []) {
    const tags = (citation as { tags?: unknown } | null)?.tags;
    if (!tags || typeof tags !== "object") continue;
    for (const dimension of SCOPE_DIMENSIONS) {
      const values = (tags as Record<string, unknown>)[dimension];
      if (!Array.isArray(values)) continue;
      for (const raw of values) {
        const value = typeof raw === "string" ? raw.trim() : "";
        const key = `${dimension}:${value.toLowerCase()}`;
        if (!value || seen.has(key)) continue;
        seen.add(key);
        out.push({ dimension, value });
      }
    }
  }
  return out;
}
